import { ChevronDown, FolderOpen, Plus } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ProjectRow {
  id: string;
  slug: string;
}

/**
 * Project chip in the header breadcrumb. Renders only when a project is
 * in the URL. Fetches the org's project list lazily on open and lets the
 * user jump between projects without leaving the current tab context.
 */
export function ProjectSwitcher() {
  const { org, project } = useParams<{ org?: string; project?: string }>();
  const nav = useNavigate();

  // Reuse the same queryKey as Dashboard so the list is shared across views.
  const projects = useQuery({
    enabled: !!org,
    queryKey: ["projects", org],
    queryFn: async () => {
      const r = await api.get<{ projects: ProjectRow[] }>(`/orgs/${org}/projects`);
      return r.projects;
    },
  });

  if (!org || !project) return null;

  const sorted = [...(projects.data ?? [])].sort((a, b) => a.slug.localeCompare(b.slug));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md hover:bg-subtle transition-colors max-w-[240px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          aria-label={`Switch project (current: ${project})`}
        >
          <span className="text-sm font-semibold truncate font-mono">{project}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-64 max-h-96 overflow-y-auto">
        <DropdownMenuLabel>Projects in {org}</DropdownMenuLabel>
        {projects.isLoading && (
          <div className="px-2 py-3 text-xs text-muted-foreground">Loading…</div>
        )}
        {!projects.isLoading && sorted.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground">No other projects</div>
        )}
        {sorted.map((p) => (
          <DropdownMenuItem
            key={p.id}
            onSelect={() => nav(`/orgs/${org}/projects/${p.slug}`)}
            className={p.slug === project ? "font-semibold bg-subtle" : undefined}
          >
            <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="truncate font-mono">{p.slug}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={`/orgs/${org}/import`}>
            <Plus className="h-3.5 w-3.5" /> Import project
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={`/orgs/${org}`}>
            <FolderOpen className="h-3.5 w-3.5" /> All projects
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
